import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { collection, doc, getDoc, getDocFromServer, onSnapshot, updateDoc } from 'firebase/firestore';
import { db } from '../firebase';
import TopBar from '../components/TopBar';
import BottomNav from '../components/BottomNav';
import Logo from '../components/Logo';
import useUserPrefs from '../hooks/useUserPrefs';
import useSelectedDevice from '../hooks/useSelectedDevice';
import usePullToRefresh from '../hooks/usePullToRefresh';
import InstallPrompt from '../components/InstallPrompt';
import LeakModal from '../components/LeakModal';
import WifiSteps from '../components/WifiSteps';
import { tankDisplayState, maToFeet } from '../utils/cal';
import { useSticky, niceStep } from '../utils/sticky';
import { isFirmwareUpdating } from '../config/firmware';
import { useLeakStatus } from '../hooks/useLeakData';
import { attemptAutoReload, reconnect, APP_RESUME_EVENT } from '../connection';
import { cachedDoc, withTimeout } from '../utils/firestoreRetry';
import GuidedTour from '../components/GuidedTour';
import { canActivateIncluded, tierOf, plusIsOn, offlineAfterMs, liveSwitchPending } from '../utils/plusStatus';
import SharingPaused from '../components/SharingPaused';
import SupplyStrip from '../components/SupplyStrip';
import PlusPromptModal from '../components/PlusPromptModal';

// Steps for the app walkthrough. Targets are matched by [data-tour="…"] on the
// live UI; steps with target:null render as a centered card. The nav targets
// resolve to whichever nav is visible (mobile bottom bar or desktop sidebar).
const TOUR_STEPS = [
  {
    target: null,
    title: 'Welcome to SoKo 👋',
    body: "Here's a quick tour of the dashboard. You can skip any time and reopen it later from the Help page.",
    primary: 'Start tour',
  },
  {
    target: '[data-tour="tank-card"]',
    title: 'Your tank at a glance',
    body: 'Shows the current level, gallons remaining, and water depth — updated automatically as your device reports in.',
  },
  {
    target: '[data-tour="nav-home"]',
    title: 'Home',
    body: 'All your tanks in one place. If you have more than one device, a row of buttons up top lets you switch between them.',
  },
  {
    target: '[data-tour="nav-trends"]',
    title: 'Trends',
    body: 'History charts and water usage over time — handy for spotting fills, draw-downs, and patterns.',
  },
  {
    target: '[data-tour="nav-settings"]',
    title: 'Settings',
    body: 'Set up your tank capacities, preferences, and device sharing.',
  },
  {
    target: '[data-tour="nav-alerts"]',
    title: 'Alerts',
    body: 'Enable text, email or push notifications. Tank level alerts, leak detection and more.',
  },
  {
    target: '[data-tour="account-menu"]',
    title: 'Your account',
    body: 'Share access with family or caretakers, switch light/dark mode, get help, or sign out — all from this menu.',
  },
  {
    target: null,
    title: "You're all set 💧",
    body: 'That\'s the basics. Next, head to Settings to finish setting up a tank. You can replay this tour anytime from the Help page.',
    primary: 'Done',
  },
];

// ── Helpers ────────────────────────────────────────────────────────────────

// Small warning triangle for the "Leak detected" badge.
function IconWarn() {
  return (
    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor"
      strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M10.29 3.86 1.82 18a1 1 0 0 0 .86 1.5h18.64a1 1 0 0 0 .86-1.5L13.71 3.86a1 1 0 0 0-1.72 0z" />
      <path d="M12 9v4" /><path d="M12 17h.01" />
    </svg>
  );
}

// Disclosure chevron appended to the leak badge — the standard "opens a
// detail view on tap" affordance (iOS/Android list rows). Signals the badge
// is clickable without adding words to an already-urgent pill.
function IconChevron() {
  return (
    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor"
      strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M9 6l6 6-6 6" />
    </svg>
  );
}

// How long the "offline" condition must hold before the banner shows. Long
// enough to outlast the reconnect/resume transient where Firestore briefly
// reports a server-confirmed-but-stale reading; short enough that a truly
// offline device still surfaces promptly.
const OFFLINE_CONFIRM_MS = 5000;

// After a PWA resume, suppress the offline banner this long so the reconnect can
// pull the current reading before we'd judge the device offline on a stale one.
const RESUME_OFFLINE_GRACE_MS = 60000;

// Badges earn their place by saying something the numbers don't. A full tank
// has no such thing to say: the percentage is already right there reading 98%,
// and the bar is already drawn to the top. So there is no 'Full' badge --
// only faults and low levels, where the badge adds urgency and a colour the
// percentage alone cannot carry.
//
// This is display only. The "Almost full" ALERT is a separate, server-side
// thing (functions/index.js, ALERT_THRESHOLDS key 'full', at >= 98%) and still
// emails/texts/pushes exactly as before.
function getBadge(pct, fault) {
  if (fault)     return { label: 'Sensor fault', cls: 'badge-red' };
  if (pct < 10)  return { label: 'Critical',     cls: 'badge-red' };
  if (pct < 20)  return { label: 'Low',          cls: 'badge-amber' };
  return null;
}

// Live dashboard alert label from the user's configured thresholds (claim doc
// alerts.thresholds). Shows the MOST SEVERE crossed level. Independent of the
// email toggle — the dashboard pill is always live; emails are gated server-side.
function activeAlertLabel(alerts, pct) {
  const t = (alerts && alerts.thresholds) || {};
  const crossed = [];
  if (t.low30 && pct < 30) crossed.push(30);
  if (t.low20 && pct < 20) crossed.push(20);
  if (t.low10 && pct < 10) crossed.push(10);
  if (t.custom && t.custom.on && typeof t.custom.value === 'number' && pct < t.custom.value) crossed.push(t.custom.value);
  if (crossed.length) return `Below ${Math.min(...crossed)}%`;
  // No 'Full' here either, for the reason in getBadge above. Ticking "Almost
  // full" on the Alerts page still turns the alert on -- it is read
  // server-side from alerts.thresholds.full; it just no longer also paints a
  // badge that repeats the percentage.
  return null;
}


// Normalize whatever the reading timestamp is (Firestore Timestamp, millis,
// {seconds}, or Date) to epoch millis. Returns null when absent.
function readingMillis(ts) {
  if (!ts) return null;
  if (typeof ts === 'number') return ts;
  if (typeof ts.toMillis === 'function') return ts.toMillis();
  if (typeof ts.seconds === 'number') return ts.seconds * 1000;
  if (ts instanceof Date) return ts.getTime();
  return null;
}

// Short date + time in Hawaii Standard Time — e.g. "7/26 · 5:09 PM".
// Time zone is fixed to Pacific/Honolulu for now (a user-selectable zone
// can come later from prefs).
const HST_DATE = new Intl.DateTimeFormat('en-US', {
  timeZone: 'Pacific/Honolulu', month: 'numeric', day: 'numeric',
});
const HST_TIME = new Intl.DateTimeFormat('en-US', {
  timeZone: 'Pacific/Honolulu', hour: 'numeric', minute: '2-digit', hour12: true,
});
function formatHST(ms) {
  if (!ms) return 'never';
  const d = new Date(ms);
  return `${HST_DATE.format(d)} · ${HST_TIME.format(d)}`;
}

// ── Depth formatting ──────────────────────────────────────────────────────

function formatDepth(depthFt, format) {
  // Format the magnitude, then prepend a proper minus sign (−, U+2212)
  // if the input was negative. Keeps the ft-in math sane for negatives
  // (otherwise Math.floor(-0.25 * 12) gives -1 ft and weird inches).
  const sign = depthFt < 0 ? '\u2212' : '';
  const abs  = Math.abs(depthFt);

  if (format === 'ft-decimal') {
    return { num: `${sign}${abs.toFixed(1)}`, unit: 'ft' };
  }
  if (format === 'inches') {
    const inches = Math.round(abs * 12 * 4) / 4;
    return { num: `${sign}${inches}`, unit: 'in' };
  }
 // Default: ft-in
  const totalInches = abs * 12;
  const ft = Math.floor(totalInches / 12);
  const inches = Math.round((totalInches - ft * 12) * 4) / 4;
  // Handle the edge case where rounding inches gives 12
  if (inches >= 12) {
    return { num: `${sign}${ft + 1} ft 0 in`, unit: '' };
  }
  // Below 1 ft, drop the feet portion — "−3 in" reads cleaner than "−0 ft 3 in"
  if (ft === 0) {
    return { num: `${sign}${inches} in`, unit: '' };
  }
  return { num: `${sign}${ft} ft ${inches} in`, unit: '' };
}

// ── Tank Card ──────────────────────────────────────────────────────────────
//
// Renders one tank. Display varies by state:
//
//   fault         — sensor fault: red, no readings
//   nothing       — no calPoints, no tank info: "Tank not calibrated" + setup CTA
//   sensor-only   — calPoints set, no tank info: live sensor depth, no % / gallons,
//                   "Finish tank setup" CTA
//   tank-only     — tank info set (button setup, no online cal yet): % works,
//                   depth doesn't, gallons works if capacity set,
//                   "Add sensor calibration for depth display" note
//   full          — everything set: normal display
//
// State is determined by cal.js tankDisplayState() against the tank object
// from devices/{deviceId}.

function TankCard({
  tankName, tank, reading, sensorPresent, fault,
  pct, mA, depthFormat, onSetup,
  alerts, offline, leak, onLeakClick,
  supply, supplyOn, supplyLocked, onSupplyLocked, supplyLabel,
}) {
  const state = tankDisplayState(tank, fault);

  // ── Display precision: derived from mA, held steady by hysteresis ────────
  //
  // ***  TO UNDO THIS ENTIRE CHANGE: set DERIVE_FROM_MA = false, below.  ***
  //
  // That one word is the whole revert. The card goes straight back to the
  // device-reported percent (p1) and every number here follows it, exactly as
  // before. No firmware change, no cloud change, no other file to touch.
  //
  // Why it exists: the firmware applies a 1.0% dead band before reporting p1,
  // so the card could only move in whole-percent jumps — 43 gal on a 4,300 gal
  // tank, ~200 gal on a 20,000 gal one. Trends reads the raw mA, so the two
  // pages disagreed by up to one dead-band step for the same instant. Deriving
  // percent and gallons from mA here is the same math Trends already uses (see
  // seriesValue in Trends.js), so they now agree exactly.
  //
  // The cost of raw mA is noise, so every displayed number goes through
  // useSticky(): hysteresis, NOT averaging. A real change still appears on the
  // very next reading with zero lag, but a value parked on a rounding boundary
  // can never flip the digit back and forth — moving it takes 0.75 of a step,
  // and moving it back takes another 0.75. Measured noise on WT-0001 is ~0.135%
  // against a 0.75% dead zone on the percent, so the percent should not jitter
  // at all. See src/utils/sticky.js for the mechanism.
  const DERIVE_FROM_MA = true;

  // Capacity → gallons. Available in 'full' and 'tank-only' states.
  // (In 'tank-only' we have mA→% but no calPoints, so % is valid;
  //  capacity × % gives gallons just like normal.)
  // Capacity entered in Settings is the tank's TOTAL volume at its full mark
  // (fullFt). Percent is the USABLE band (0% = emptyFt, 100% = fullFt), so
  // usable gallons scales total capacity by the usable fraction of full height:
  //   usableCapacity = capacity · (fullFt − emptyFt) / fullFt
  const capacity = tank?.capacity ?? 0;
  const hasBand = typeof tank?.fullFt === 'number' && tank.fullFt > 0
    && typeof tank?.emptyFt === 'number';
  const usableFt   = hasBand ? tank.fullFt - tank.emptyFt : 0;
  const usableFrac = hasBand ? usableFt / tank.fullFt : 1;

  // Absolute sensor depth from the live mA reading. null when there are no
  // calPoints ('tank-only'/'nothing') or no usable reading.
  const absFt = maToFeet(mA, tank?.calPoints);

  // Fine-grained percent from mA, falling back to the device's dead-banded p1
  // whenever we can't derive one (no calPoints, no reading, or flag off).
  const rawPct = (DERIVE_FROM_MA && state === 'full' && absFt !== null && usableFt > 0)
    ? (absFt - tank.emptyFt) / usableFt * 100
    : pct;

  // Raw usable depth, in feet — derivation depends on state.
  //   full:        usable depth = mA→ft(calPoints) − emptyFt
  //                (allowed to go negative — sensor below pickup level)
  //   sensor-only: absolute sensor depth = mA→ft(calPoints) (clamped at 0)
  //   tank-only:   no feet derivation possible (no calPoints)
  //   nothing:     no feet
  const rawDepthFt =
    (state === 'full' && absFt !== null && typeof tank?.emptyFt === 'number')
      ? absFt - tank.emptyFt :
    (state === 'sensor-only' && absFt !== null)
      ? Math.max(0, absFt) :
      null;
  const depthLabel =
    rawDepthFt === null     ? 'Depth' :
    state === 'sensor-only' ? 'Sensor depth' :
                              'Usable depth';

  // Clamp to 0 minimum so gallons never display negative — pct can go
  // negative (tank below empty setting), but "negative gallons" reads as a bug.
  const rawGal = ((state === 'full' || state === 'tank-only') && capacity > 0)
    ? Math.max(0, rawPct) / 100 * capacity * usableFrac
    : null;

  // Display steps — the resolution each number is allowed to move in.
  //   percent: whole numbers, as before.
  //   depth:   whatever formatDepth would have rounded to anyway (¼ in, or
  //            0.1 ft in decimal mode), so this changes nothing visually.
  //   gallons: a human-sized step scaled to the tank — ~10 gal on a 4,300 gal
  //            tank, 50 gal on a 20,000 gal one. Still 4–5× finer than the 1%
  //            dead band it replaces, while staying wider than sensor noise.
  const depthStep = depthFormat === 'ft-decimal' ? 0.1 : 0.25 / 12;
  const galStep   = niceStep((capacity * usableFrac) / 200);

  // These three hooks must run on every render, in this order, whatever the
  // state — useSticky returns null for a value we can't show yet.
  const stickyPct = useSticky(rawPct, 1);
  const depthFt   = useSticky(rawDepthFt, depthStep);
  const volGal    = useSticky(rawGal, galStep);

  const displayPct = stickyPct !== null ? stickyPct : rawPct;
  // Bar clamps to [0, 100] — bar can't physically go below empty or above full,
  // even when the numeric pct does (setup mismatch). Numbers tell the truth;
  // the bar tells the physical story. Driven by the raw value, not the sticky
  // one: a bar height has no digits to flicker, so it may as well be smooth.
  const barHeight   = Math.min(Math.max(rawPct, 0), 100);

  // Fill tier — drives bar color
  const fillTier =
    displayPct < 10  ? 'critical' :
    displayPct < 20  ? 'low' :
    displayPct < 50  ? 'half' :
                       'normal';

  // Badge shown only in 'full' state (top-right of card)
  const badge = state === 'full' ? getBadge(displayPct, fault) : null;

  // A user-configured threshold alert takes over the badge slot when crossed.
  const alertLabel = (state === 'full' || state === 'tank-only')
    ? activeAlertLabel(alerts, displayPct) : null;
  // alertLabel is only ever a "Below N%" now, so the colour follows severity.
  const shownBadge = alertLabel
    ? { label: alertLabel, cls: displayPct < 10 ? 'badge-red' : 'badge-amber' }
    : badge;

  const depth = depthFt !== null ? formatDepth(depthFt, depthFormat) : null;

  // Setup-mismatch hints in 'full' state. Shown in copper italic under the
  // percent. Replaces the older "Check calibration" warning — these tell the
  // user *what* is off (their empty/full feet settings) instead of just
  // flagging that something is. 2% buffer so jitter near the boundaries
  // doesn't flicker the message in and out.
  const belowEmpty = state === 'full' && rawPct < -2;
  const aboveFull  = state === 'full' && rawPct > 105;

  // ── Render ───────────────────────────────────────────────────────────
  // Classes: 'uncalibrated' is the existing CSS hook for any state where
  // the bar is dim and the percent is muted. Applies to nothing/sensor-only/
  // tank-only — anything that isn't fully configured.
  const cardClass =
    state === 'fault' ? 'card fault' :
    state === 'full'  ? 'card' :
                        'card uncalibrated';

  return (
    <div className={cardClass} data-tour="tank-card">

      {/* Header. A leak (rapid, slow, or away) takes over the badge slot — it's
          the most urgent thing we can say about the tank, so it wins over the
          low/critical level badge (the level number + color already say "low"). */}
      <div className="card-header">
        <div className="card-title">{tankName}</div>
        {leak && leak.active ? (
          <button type="button" className="badge badge-leak" title="View details"
            aria-label={`${leak.kind === 'rapid' ? 'Usage alert' : 'Leak detected'} — view details`}
            onClick={() => onLeakClick && onLeakClick({ leak, tankName, pct: rawPct })}>
            <IconWarn /> {leak.kind === 'rapid' ? 'Usage alert' : 'Leak detected'}
            <span className="badge-leak__chev" aria-hidden="true"><IconChevron /></span>
          </button>
        ) : shownBadge ? (
          <span className={`badge ${shownBadge.cls}`}>{shownBadge.label}</span>
        ) : null}
      </div>

      {/* Bar + percentage row */}
      <div className="tank-row">

        {/* Vertical bar */}
        <div className={`tank-bar-vertical ${state !== 'full' && state !== 'tank-only' ? 'tank-bar-vertical--placeholder' : ''}`}>
          <div
            className={`tank-bar-vertical__fill tank-bar-vertical__fill--${fillTier}`}
            style={{
              height: (state === 'fault' || state === 'nothing' || state === 'sensor-only')
                ? 0
                : `${barHeight}%`,
            }}
          />
        </div>

        {/* Percentage + status — per state */}
        {state === 'fault' && (
          <div>
            <div className="tank-pct tank-pct--fault">--</div>
            <div className="level-status fault">Check wiring or sensor</div>
          </div>
        )}

        {state === 'nothing' && (
          <div>
            <div className="tank-pct tank-pct--uncal">
              <span className="tank-pct__num">—</span>
              <span className="tank-pct__unit">%</span>
            </div>
            <div className="level-status level-status--uncal">
              Tank not calibrated
            </div>
            <div className="level-status-hint">Tap below to set up</div>
          </div>
        )}

        {state === 'sensor-only' && (
          <div>
            <div className="tank-pct tank-pct--uncal">
              <span className="tank-pct__num">—</span>
              <span className="tank-pct__unit">%</span>
            </div>
            <div className="level-status level-status--uncal">
              Tank setup needed
            </div>
            <div className="level-status-hint">Tap below to finish</div>
          </div>
        )}

        {state === 'tank-only' && (
          <div>
            <div className="tank-pct">
              <span className="tank-pct__num">{Math.round(displayPct)}</span>
              <span className="tank-pct__unit">%</span>
            </div>
            <div className="level-status-hint">Add sensor cal for depth</div>
          </div>
        )}

        {state === 'full' && (
          <div>
            <div className={`tank-pct ${belowEmpty ? 'tank-pct--uncal' : ''}`}>
              <span className="tank-pct__num">
                {belowEmpty ? 0 : Math.round(displayPct)}
              </span>
              <span className="tank-pct__unit">%</span>
            </div>
            {belowEmpty && (
              <div className="level-status level-status--setup">
                Below your empty setting
              </div>
            )}
            {aboveFull && (
              <div className="level-status level-status--setup">
                Above your full setting
              </div>
            )}
          </div>
        )}
      </div>

      {/* Stats row — hidden for fault state */}
      {state !== 'fault' && (
        <>
          <div className="tank-divider" />
          {state === 'full' && <div className="tank-stats-head">Usable water</div>}
          <div className="tank-stats">

            {/* Volume */}
            <div className="tank-stat">
              <div className="tank-stat__label">Gallons</div>
              {volGal !== null ? (
                <div className="tank-stat__value">
                  <span className="tank-stat__num">{volGal.toLocaleString()}</span>
                  <span className="tank-stat__unit">gal</span>
                </div>
              ) : (state === 'full' || state === 'tank-only') ? (
                <button
                  type="button"
                  className="tank-stat__cta"
                  onClick={() => window.location.href = '/settings'}
                >
                  + Set capacity
                </button>
              ) : (
                <div className="tank-stat__value tank-stat__value--muted">—</div>
              )}
            </div>

            <div className="tank-stat-divider" />

            {/* Depth */}
            <div className="tank-stat">
              <div className="tank-stat__label">{depthLabel}</div>
              {depth ? (
                <div className={`tank-stat__value ${
                  state === 'sensor-only' ? 'tank-stat__value--est' :
                  (depthFt !== null && depthFt < 0) ? 'tank-stat__value--setup' :
                  ''
                }`}>
                  <span className="tank-stat__num">{depth.num}</span>
                  {depth.unit && <span className="tank-stat__unit">{depth.unit}</span>}
                </div>
              ) : (
                <div className="tank-stat__value tank-stat__value--muted">—</div>
              )}
              {state === 'sensor-only' && (
                <div className="tank-stat__est-note">From sensor only</div>
              )}
            </div>

          </div>

          {/* Time until empty — trial monitors only (admin switch). */}
          {supplyOn && state === 'full' && (
            <SupplyStrip
              est={supply}
              gallons={volGal}
              pct={displayPct}
              alerts={alerts}
              offline={offline}
              locked={supplyLocked}
              onLocked={onSupplyLocked}
              tankLabel={supplyLabel}
            />
          )}

          {/* Setup CTA — shown for partial/missing states */}
          {(state === 'nothing' || state === 'sensor-only') && (
            <button
              type="button"
              className="tank-card-setup-cta"
              onClick={onSetup}
            >
              {state === 'nothing' ? 'Set up your tank →' : 'Finish tank setup →'}
            </button>
          )}
        </>
      )}
    </div>
  );
}

// ── Main Dashboard ─────────────────────────────────────────────────────────

export default function Dashboard({ user }) {
  const { prefs, loaded: prefsLoaded, updatePref } = useUserPrefs(user);
  const [searchParams, setSearchParams] = useSearchParams();
  const [tourRun, setTourRun] = useState(false);

  // Start the tour when either the user asked for it (Help → /dashboard?tour=1)
  // or they're new (no tourSeen flag yet). The welcome step is a gentle,
  // dismissible popup, matching the "pop up for new users" ask.
  useEffect(() => {
    if (!prefsLoaded) return;
    if (searchParams.get('tour') === '1') { setTourRun(true); return; }
    if (!prefs.tourSeen) setTourRun(true);
  }, [prefsLoaded, prefs.tourSeen, searchParams]);

  const finishTour = useCallback(() => {
    setTourRun(false);
    if (!prefs.tourSeen) updatePref('tourSeen', true);
    if (searchParams.get('tour')) {
      searchParams.delete('tour');
      setSearchParams(searchParams, { replace: true });
    }
  }, [prefs.tourSeen, updatePref, searchParams, setSearchParams]);

  const [devices, setDevices]       = useState([]);
  const [loading, setLoading]       = useState(true);
  // Set if the device list never resolves within the watchdog window — flips the
  // spinner to a minimal "Reload" fallback so a load can't strand us.
  const [loadError, setLoadError]   = useState(false);
  const [selected, setSelected]     = useSelectedDevice(user, devices);
  const [reading, setReading]       = useState(null);
  const [deviceData, setDeviceData] = useState({});  // tank1, tank2 from devices/{id}
  // True once the SELECTED device's first snapshot has arrived (cache or
  // server). Gates the main view so it never flashes empty/0% tank cards in
  // the window after the device list resolves but before telemetry loads.
  const [deviceReady, setDeviceReady] = useState(false);
  // Whether the current telemetry snapshot came from the local cache (vs a
  // server-confirmed read). We never judge "offline" from cached data — on a
  // PWA reopen the cache holds an old lastReading whose age would wrongly trip
  // the offline check for a second until the live snapshot arrives.
  const [readingFromCache, setReadingFromCache] = useState(true);
  const navigate                     = useNavigate();

  // ── Load user's claimed devices ───────────────────────
  // Under v8: claims live at users/{uid}/devices subcollection.
  // Each claim has {deviceId, role, claimedAt, ...}. Device-level
  // names live on devices/{id}; the dashboard's per-device pills
  // need them, so we fetch each device doc alongside the claim.
  useEffect(() => {
    if (!user) return;
    let cancelled = false;

    // Watchdog: if neither a cache nor a server snapshot resolves the list
    // within this window, the underlying read has stalled — surface a retry
    // card instead of spinning forever. Cleared on the first real emission.
    const watchdog = setTimeout(() => {
      if (cancelled) return;
      // Try to self-heal with a guarded reload first; only show the error card if
      // that's suppressed (offline / just reloaded), so it can't loop.
      if (!attemptAutoReload()) { setLoadError(true); setLoading(false); }
    }, 8000);
    const settle = () => { clearTimeout(watchdog); if (!cancelled) setLoadError(false); };

    // Live listener (not a one-shot read) so the picker self-heals if the
    // first result is empty or stale — e.g. right after a password reset,
    // when Firebase has just revoked the old token and it's still settling.
    // A single getDocs there could come back empty and never retry, which
    // showed up as "devices gone until a hard refresh."
    const unsub = onSnapshot(
      collection(db, 'users', user.uid, 'devices'),
      async (claimsSnap) => {
        settle();
        // Ignore a cache-only EMPTY first emission. On a cold cache (e.g. a
        // brand-new login) onSnapshot fires from cache first with no docs,
        // which would flash "No devices yet"; the server snapshot arrives
        // right after. Wait for it (or any non-empty result) so we never show
        // the empty state on a false negative. A genuinely device-less user
        // still gets the server emission (fromCache=false) and the real
        // empty state.
        if (claimsSnap.empty && claimsSnap.metadata.fromCache) return;
        const claims = claimsSnap.docs.map((d) => ({ id: d.id, ...d.data() }));

        // Cache-first: paint the picker immediately using device names from the
        // LOCAL cache (or the deviceId as a fallback). cachedDoc never touches
        // the network, so the list never blocks on a server read that can hang
        // after the phone wakes — this is what stops the resume stall here.
        const cachedList = await Promise.all(claims.map(async (c) => {
          const snap = await cachedDoc(doc(db, 'devices', c.deviceId));
          return { ...c, name: (snap && snap.exists()) ? (snap.data().name || c.deviceId) : c.deviceId };
        }));
        if (!cancelled) { setDevices(cachedList); setLoading(false); }

        // Then upgrade names from the server in the BACKGROUND — repaints when it
        // returns, but never blocks the UI (a stalled read just leaves the cached
        // names in place until the connection recovers).
        Promise.all(claims.map(async (c) => {
          try {
            const snap = await getDoc(doc(db, 'devices', c.deviceId));
            return { ...c, name: snap.exists() ? (snap.data().name || c.deviceId) : c.deviceId };
          } catch {
            return { ...c, name: c.deviceId };
          }
        })).then((fresh) => { if (!cancelled) setDevices(fresh); }).catch(() => {});
      },
      (err) => {
        console.error('Error loading devices:', err);
        clearTimeout(watchdog);
        if (!cancelled && !attemptAutoReload()) { setLoadError(true); setLoading(false); }
      }
    );

    return () => { cancelled = true; clearTimeout(watchdog); unsub(); };
  }, [user]);

  // Device doc → page state. Shared by the live listener, pull-to-refresh and
  // the stale-reading check, so all three read the doc the same way.
  const applyDevice = useCallback((data) => {
    setReading(data.lastReading || null);
    setDeviceData({
      name:  data.name,
      model: data.model || null,
      lastSeen: data.lastSeen || null,
      // Steady push cadence (seconds), set per device at provisioning
      // (30 standard, 300 for 5-minute units). Scales the offline
      // threshold below so a slow-cadence device isn't flagged offline
      // between pushes. Absent → fall back to 30 in isOffline.
      steadyPollSec: data.steadyPollSec ?? null,
      fw: data.fw || null,
      tank1: data.tank1 || {},
      tank2: data.tank2 || {},
      // Paid-tier flag — gates the leak-status read (badge). Without it the
      // gate is always false and no leak badge ever shows.
      leakDetection: data.leakDetection === true,
      // Plus state (cloud-written) — drives the "activate your included
      // year" banner.
      plus: data.plus || null,
      // Free tier: the once-a-day 06:00 reading (cloud-written), and what the
      // tier depends on — claimed + claimedAt (first 24 h run at full speed).
      dailyReading: data.dailyReading || null,
      claimed: data.claimed === true,
      claimedAt: data.claimedAt || null,
      ownerEmail: data.ownerEmail || null,
      // Time until empty (trial): the admin switch, and each tank's daily-use
      // rate from the cloud.
      features: data.features || {},
      supply: data.supply || null,
      // Live rapid-leak state, updated by the cloud on every push: the
      // event in progress per tank, and the last one that settled.
      usageEpisode: data.usageEpisode || null,
      usageLastSettled: data.usageLastSettled || null,
    });
  }, []);

  // Bumped to re-open the live listener after its connection went quiet (see
  // resync). A re-open keeps what's on screen: no spinner, no cache flash.
  const [listenerKey, setListenerKey] = useState(0);
  const reopeningRef = useRef(false);
  // Just confirmed with the server: ignore the re-opened listener's first,
  // cache-only emission (it would briefly look "unconfirmed" and flicker the
  // offline state) until the listener's own server emission arrives.
  const keepServerStateRef = useRef(false);
  // The reading the server gave us at the last resync. Until the listener
  // catches up, an emission carrying an OLDER reading (a cache replay, a lagging
  // stream) is ignored rather than rolling the screen back.
  const resyncReadingMsRef = useRef(null);

  // ── Subscribe to live readings + device data ──────────
  // Single snapshot covers lastReading (telemetry) and tank{1,2}
  // (cal points, tank info, names, capacity). The tank state machine
  // is computed from these on each render.
  useEffect(() => {
    if (!selected) return;
    if (!reopeningRef.current) {
      setDeviceReady(false);
      setReadingFromCache(true);
      keepServerStateRef.current = false;
      resyncReadingMsRef.current = null;
    }
    reopeningRef.current = false;
    const unsub = onSnapshot(
      doc(db, 'devices', selected),
      // includeMetadataChanges so the cache→server transition is delivered
      // even when the data is unchanged (a genuinely offline device) — that's
      // what flips readingFromCache to false. Same server read; no extra cost.
      { includeMetadataChanges: true },
      (snap) => {
        const data = snap.exists() ? snap.data() : null;
        if (data && keepServerStateRef.current && resyncReadingMsRef.current) {
          const ms = readingMillis(data.lastReading && data.lastReading.timestamp) ?? readingMillis(data.lastSeen);
          if (ms && ms < resyncReadingMsRef.current) return;
        }
        if (data) applyDevice(data);
        // Only trust "offline" from a server-confirmed read, not the cache.
        if (!(snap.metadata.fromCache && keepServerStateRef.current)) {
          setReadingFromCache(snap.metadata.fromCache);
          if (!snap.metadata.fromCache) keepServerStateRef.current = false;
        }
        // Mark ready on the first emission (even a cache-only or missing-doc
        // one) so the main view can render without flashing empty tanks.
        setDeviceReady(true);
      },
      (err) => { console.error('Snapshot error:', err); setDeviceReady(true); }
    );
    return unsub;
  }, [selected, listenerKey, applyDevice]);

  // ── Is the reading old, or is OUR connection? ─────────
  // On an iPhone left open, iOS can quietly drop the page's Firestore
  // connection (screen dims, Wi-Fi/cellular hand-off, the PWA throttled) with
  // no event the app hears. The live listener then just stops delivering, the
  // last reading ages, and it looks like the DEVICE went offline when it's the
  // phone's connection that went stale. resync() asks the server directly,
  // with a time limit:
  //   answers  → show the server's copy and re-open the listener ('ok')
  //   silent   → re-establish the connection and ask once more; still silent
  //              → a guarded reload (fast: paints from cache, reconnects
  //              cleanly) or, if a reload just happened, 'unreachable'
  // One extra read, and only when something already looks wrong.
  const RESYNC_TIMEOUT_MS = 7000;
  const selectedNowRef = useRef(selected);
  useEffect(() => { selectedNowRef.current = selected; }, [selected]);
  const resync = useCallback(async () => {
    if (!selected) return { status: 'ok', readingMs: null };
    const deviceId = selected;
    const ref = doc(db, 'devices', deviceId);
    const askServer = async () => {
      const snap = await withTimeout(getDocFromServer(ref), RESYNC_TIMEOUT_MS);
      // They switched monitors while we waited: don't paint this one's data.
      if (selectedNowRef.current !== deviceId) return { status: 'ok', readingMs: null };
      const data = snap.exists() ? snap.data() : null;
      if (data) applyDevice(data);
      setReadingFromCache(false);
      keepServerStateRef.current = true;
      reopeningRef.current = true;
      setListenerKey((k) => k + 1);
      const r = data && data.lastReading;
      const readingMsNow = readingMillis(r && r.timestamp) ?? readingMillis(data && data.lastSeen);
      resyncReadingMsRef.current = readingMsNow || null;
      return { status: 'ok', readingMs: readingMsNow };
    };
    try {
      return await askServer();
    } catch (e1) {
      try { await reconnect(); } catch { /* best-effort */ }
      try {
        return await askServer();
      } catch (e2) {
        console.warn('Dashboard: server unreachable after reconnect', e2);
        // One automatic reload per 5 minutes from this path. A reload fixes a
        // connection iOS killed; if the network itself is down (or a captive
        // Wi-Fi page), reloading every check would just loop, so after one
        // try we say "Can't reach SoKo" and let them pull to retry.
        const KEY = 'soko.resyncReloadAt';
        let last = 0;
        try { last = Number(sessionStorage.getItem(KEY) || 0); } catch { /* fine */ }
        if (Date.now() - last > 5 * 60 * 1000) {
          try { sessionStorage.setItem(KEY, String(Date.now())); } catch { /* fine */ }
          if (attemptAutoReload()) return { status: 'reloading' };
        }
        return { status: 'unreachable' };
      }
    }
  }, [selected, applyDevice]);

  // Live "updated X ago" counter
  const [, forceUpdate] = useState(0);
  useEffect(() => {
    const interval = setInterval(() => forceUpdate(n => n + 1), 10000);
    return () => clearInterval(interval);
  }, []);

  const currentDevice = devices.find(d => d.deviceId === selected);

  // "Your first year of Plus is included" — owner, monitor not comped and its
  // included year not used yet. "Not now" hides it on this device for 14 days
  // (per browser; a convenience, so storage failing just shows it again).
  const PLUS_NUDGE_SNOOZE_MS = 14 * 86400000;
  const plusNudgeKey = selected ? `soko.plusNudge.${selected}` : null;
  const [plusNudgeSnoozed, setPlusNudgeSnoozed] = useState(false);
  const [plusPromptOpen, setPlusPromptOpen] = useState(false);
  useEffect(() => {
    if (!plusNudgeKey) return;
    try {
      const at = Number(window.localStorage.getItem(plusNudgeKey) || 0);
      setPlusNudgeSnoozed(at > 0 && Date.now() - at < PLUS_NUDGE_SNOOZE_MS);
    } catch { setPlusNudgeSnoozed(false); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [plusNudgeKey]);
  const snoozePlusNudge = () => {
    setPlusNudgeSnoozed(true);
    try { window.localStorage.setItem(plusNudgeKey, String(Date.now())); } catch { /* fine */ }
  };
  const showPlusNudge = deviceReady && currentDevice?.role === 'owner'
    && canActivateIncluded(deviceData.plus) && !plusNudgeSnoozed;
  // Leak detail modal (opened by tapping the badge). Holds { leak, tankName }.
  const [leakModal, setLeakModal] = useState(null);
  const viewLeakOnChart = () => {
    // Deep-link to Trends at the 48h range — leaks clear at 48h, so that range
    // always frames the region. Range is read from localStorage on Trends mount.
    try { window.localStorage.setItem('trends.range', '48h'); } catch { /* ignore */ }
    setLeakModal(null);
    navigate('/trends');
  };
  // Dismiss a SETTLED leak badge for THIS user. Records a per-kind, per-tank
  // "dismissed-through = now" on the viewer's own claim doc, so the dashboard
  // badge clears (across their devices) while Trends and the sent alert stay put;
  // a fresh drop after this still shows. Only offered by the modal when settled.
  const dismissLeak = useCallback(async () => {
    const lk = leakModal && leakModal.leak;
    setLeakModal(null);
    if (!lk || !user || !selected || !lk.kind || !lk.tankNum) return;
    try {
      await updateDoc(doc(db, 'users', user.uid, 'devices', selected), {
        [`alerts.leakDismiss.${lk.kind}.${lk.tankNum}`]: Date.now(),
      });
    } catch (e) {
      console.error('Dismiss leak failed:', e);
    }
  }, [leakModal, user, selected]);
  // Real reading time (from the reading itself, falling back to the device’s
  // lastSeen heartbeat) — drives the "last updated" line and offline detection,
  // so neither reflects page-load time anymore.
  const readingMs = readingMillis(reading?.timestamp) ?? readingMillis(deviceData.lastSeen);
  // Only flag offline once a server-confirmed snapshot says the reading is
  // genuinely stale — never from a cache-first emission (avoids the reopen flash).
  // The staleness threshold scales with the device's push cadence so a slower
  // (tier-gated) 5-minute unit isn't flagged offline between every push. Falls
  // back to 30s when the device doc has no steadyPollSec yet (today's fleet):
  //   30s device  → max(120s, 150s)  = 150s  (5 missed pushes)
  //   300s device → max(120s, 1500s) = 25min (5 missed pushes)
  // Free (no Plus, past its first day): hourly reporting, and the cards show
  // the once-a-day 06:00 reading. The offline check uses the REAL latest
  // reading (readingMs) at the hourly cadence; only the display is throttled.
  const tier = tierOf({ plus: deviceData.plus, claimed: deviceData.claimed, claimedAt: deviceData.claimedAt });
  const freeDaily = tier === 'free' ? (deviceData.dailyReading || null) : null;
  const shown = freeDaily || reading;
  const shownMs = freeDaily ? (readingMillis(freeDaily.timestamp) ?? readingMs) : readingMs;
  // Sharing is a Plus feature: a shared user of a monitor without Plus sees
  // why it's paused instead of the tanks (the cloud stops their alerts too).
  const sharingPaused = deviceReady && !!currentDevice && currentDevice.role !== 'owner'
    && !plusIsOn(deviceData.plus);
  // Offline after 5 missed reports at the interval the monitor is on (hourly
  // on Free, and right after SoKo Plus starts on an hourly monitor until it
  // checks in). Same rule as the cloud's admin view (billing.offlineAfterMs).
  const offlineThresholdMs = offlineAfterMs({
    plus: deviceData.plus, claimed: deviceData.claimed, claimedAt: deviceData.claimedAt,
    steadyPollSec: deviceData.steadyPollSec,
  }, readingMs);
  // SoKo Plus just started on a monitor still on hourly reporting: say so,
  // and how to start live updates now.
  const switchingToLive = !sharingPaused && liveSwitchPending(deviceData.plus, readingMs);
  // Device is on older firmware → it's updating (or about to) over WiFi.
  const isUpdating = isFirmwareUpdating(deviceData.fw);
  // After the PWA resumes from a background, the device doc can briefly deliver a
  // server-confirmed snapshot still carrying the PRE-sleep reading (the fresh push
  // hasn't synced yet). That's an app-sync lag, not a device outage — so suppress
  // the offline banner for a short grace period after a resume, giving the
  // reconnect time to pull the current reading. A genuinely offline device still
  // surfaces once the grace passes.
  const [resumeGraceUntil, setResumeGraceUntil] = useState(0);
  useEffect(() => {
    const onResume = () => setResumeGraceUntil(Date.now() + RESUME_OFFLINE_GRACE_MS);
    window.addEventListener(APP_RESUME_EVENT, onResume);
    return () => window.removeEventListener(APP_RESUME_EVENT, onResume);
  }, []);
  const offlineNow = !readingFromCache && !!readingMs
    && (Date.now() - readingMs) > offlineThresholdMs
    && Date.now() > resumeGraceUntil;

  // Debounce the offline flag on its rising edge so a transient never flashes.
  // The !readingFromCache gate stops the plain cache→server case, but on
  // resume/reconnect Firestore can briefly deliver a server-confirmed snapshot
  // still carrying the stale cached reading before the fresh one syncs — that
  // shows as (fromCache=false, old reading) for a moment. Requiring the
  // condition to hold for OFFLINE_CONFIRM_MS lets that transient clear without
  // ever showing the banner; a genuinely offline device still surfaces after
  // the short delay. Clears immediately the moment a fresh reading arrives.
  //
  // And before saying "Device offline" at all, check it isn't our own stale
  // connection (resync, above). Only a fresh answer from the server that still
  // carries an old reading means the device really stopped reporting. If the
  // server can't be reached, say that instead — it's the phone's connection.
  const [isOffline, setIsOffline] = useState(false);
  const [unreachable, setUnreachable] = useState(false);
  // The reading the server last confirmed as old, so a device that really is
  // offline is checked once, not on every re-render or listener re-open.
  const confirmedStaleRef = useRef(null);
  useEffect(() => {
    if (!offlineNow) { setIsOffline(false); setUnreachable(false); return undefined; }
    if (confirmedStaleRef.current && confirmedStaleRef.current === readingMs) { setIsOffline(true); return undefined; }
    let cancelled = false;
    const t = setTimeout(async () => {
      const r = await resync();
      if (cancelled || r.status === 'reloading') return;
      if (r.status === 'unreachable') { setUnreachable(true); return; }
      // The server's own copy: a fresh reading clears offlineNow on the next
      // render; one that's still old means the device really is offline.
      if (r.readingMs && Date.now() - r.readingMs > offlineThresholdMs) {
        confirmedStaleRef.current = r.readingMs;
        setIsOffline(true);
      }
    }, OFFLINE_CONFIRM_MS);
    return () => { cancelled = true; clearTimeout(t); };
    // offlineThresholdMs only changes with the device's cadence.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [offlineNow, resync]);

  // Per-tank leak status for the badges. Reads leakStats only for leak-enabled
  // devices (0 reads otherwise); the ?leakdemo mock overrides for preview. The
  // live rapid event rides on the device snapshot above, so the badge and the
  // modal follow it push by push with no extra reads.
  const leakLive = useMemo(() => ({
    episodes: deviceData.usageEpisode || null,
    lastSettled: deviceData.usageLastSettled || null,
    offline: isOffline,
    lastSeenMs: readingMs || null,
  }), [deviceData.usageEpisode, deviceData.usageLastSettled, isOffline, readingMs]);
  const leakStatus = useLeakStatus(currentDevice?.deviceId, deviceData?.leakDetection === true,
    currentDevice?.alerts, leakLive);

  // Pull-to-refresh: data is already live via onSnapshot, so this asks the
  // server directly (with a time limit, so the spinner can never hang) and
  // re-opens the listener — the same check as before an offline banner.
  const doRefresh = useCallback(async () => {
    const r = await resync();
    if (r.status === 'unreachable') setUnreachable(true);
    else if (r.status === 'ok') setUnreachable(false);
    await new Promise((res) => setTimeout(res, 450));  // keep the spinner visible briefly
  }, [resync]);
  const { pull, refreshing } = usePullToRefresh(doRefresh);

  // ── Render ───────────────────────────────────────────────────────────────

  if (loadError) {
    return (
      <div className="app-container">
        <TopBar showLive={false} />
        <div className="loading-page">
          <p style={{ margin: '0 0 14px' }}>Couldn't load your devices.</p>
          <button type="button" className="btn btn-primary" onClick={() => window.location.reload()}>
            Reload
          </button>
        </div>
        <BottomNav />
      </div>
    );
  }

  if (loading) {
    return (
      <div className="app-container">
        <TopBar showLive={false} />
        <div className="loading-page"><div className="spinner" /></div>
        <BottomNav />
      </div>
    );
  }

  // No devices yet
  if (devices.length === 0) {
    return (
      <div className="app-container">
        <TopBar subtitle="No devices" />
        <div className="page-content">
          <div className="empty-state">
            <div className="empty-state-icon">
              <Logo variant="icon" width={64} />
            </div>
            <h3>No devices yet</h3>
            <p>Connect your tank monitor to start tracking your water levels.</p>
            <button
              type="button"
              className="btn btn-primary"
              style={{ width: 'auto', padding: '10px 24px' }}
              onClick={() => window.location.href = '/devices'}
            >
              + Add your first device
            </button>
          </div>
        </div>
        <BottomNav />
      </div>
    );
  }

  // Devices exist, but the selected device's telemetry hasn't arrived yet
  // (selection resolving, or first snapshot pending). Keep the spinner up so
  // we never flash empty/0% tank cards before real data.
  if (!selected || !deviceReady) {
    return (
      <div className="app-container">
        <TopBar showLive={false} />
        <div className="loading-page"><div className="spinner" /></div>
        <BottomNav />
      </div>
    );
  }

  // Same rule as offline: don't raise the fault alarm from cached data on a
  // reopen (a fault that has since cleared would flash red for ~1s). Only
  // trust it once the snapshot is server-confirmed.
  const hasFault  = !readingFromCache && (reading?.f1 || reading?.f2);

  // Prefer the device doc's name (subscribed via onSnapshot, lives on
  // devices/{id}.name) over the cached one from the claim load — that
  // way name edits in Settings flow through immediately.
  const displayName = deviceData.name || currentDevice?.name || '';

  // Single-tank product models ("ST, etc.) have no tank-2 hardware —
  // don't render the "no sensor detected" strip for them. A missing model
  // field falls back to legacy behavior (treat as dual-capable), and a
  // dual-tank model with s2=false still shows the strip (real fault info).
  const isSingleTank = (deviceData.model || '').startsWith('ST');

  return (
    <div className="app-container">
      <TopBar
        subtitle={displayName}
        showLive={false}
        rightContent={hasFault
          ? <span className="topbar-fault">⚠ Fault</span>
          : null
        }
      />

      <div className="page-content">

        {/* Pull-to-refresh indicator */}
        {(pull > 0 || refreshing) && (
          <div className="ptr-indicator" style={{ height: refreshing ? 44 : pull }}>
            <div className={`ptr-spinner ${refreshing ? 'ptr-spinner--spin' : ''}`} style={{ opacity: refreshing ? 1 : Math.min(1, pull / 70) }} />
          </div>
        )}

        {/* Device selector — shown if user has multiple devices */}
        {devices.length > 1 && (
          <div className="device-selector">
            {devices.map(d => (
              <button
                key={d.deviceId}
                type="button"
                className={`device-pill ${selected === d.deviceId ? 'active' : ''}`}
                onClick={() => { setSelected(d.deviceId); setReading(null); }}
              >
                {d.name}
              </button>
            ))}
          </div>
        )}

        {/* Offline banner — takes precedence over "updating". If a device is
            offline we can't confirm it's actually updating (it's dark), so show
            the real outage rather than an optimistic "updating". */}
        {/* Our connection, not the device: the server didn't answer even after
            reconnecting (and a reload just happened). Says so plainly. */}
        {unreachable && !isOffline && !sharingPaused && (
          <div className="alert-banner info">
            <div className="alert-banner-title">Can't reach SoKo right now</div>
            <div className="alert-banner-desc">
              Check your phone's internet connection. Pull down to try again.
            </div>
          </div>
        )}

        {isOffline && !sharingPaused && (
          <div className="alert-banner warn">
            <div className="alert-banner-title">Device offline</div>
            <div className="alert-banner-desc">
              Last seen {formatHST(readingMs)}. Check that it&apos;s powered on. To
              re-connect it to wifi:
              <div className="offline-reset">
                <WifiSteps compact />
              </div>
              <Link to="/install" className="offline-reset__link">Full setup instructions</Link>
            </div>
          </div>
        )}

        {switchingToLive && !isOffline && (
          <div className="alert-banner info">
            <div className="alert-banner-title">Switching to live updates</div>
            <div className="alert-banner-desc">
              It can take up to an hour for SoKo Plus changes to take effect on your monitor.
              To start now, give the button on your monitor a short press.
            </div>
          </div>
        )}

        {/* Updating banner — device is on older firmware and updating over WiFi.
            Only shown when it's not offline. Power loss mid-update is harmless
            (it just reboots and retries), so no "keep it powered" ask. */}
        {isUpdating && !isOffline && !sharingPaused && (
          <div className="alert-banner info">
            <div className="alert-banner-title">Your device is updating</div>
            <div className="alert-banner-desc">
              It will reboot automatically — no action required.
            </div>
          </div>
        )}

        {/* Included year of Plus not activated yet (owner only). */}
        {showPlusNudge && (
          <div className="alert-banner info plus-nudge">
            <div className="alert-banner-title">Your first year of SoKo Plus is included</div>
            <div className="alert-banner-desc">
              Usage and leak alerts, sharing and a monthly usage summary. $0 today.
            </div>
            <div className="plus-nudge__actions">
              <button type="button" className="btn btn-primary plus-nudge__btn"
                onClick={() => setPlusPromptOpen(true)}>Activate SoKo Plus</button>
              <button type="button" className="linklike plus-nudge__later" onClick={snoozePlusNudge}>Not now</button>
            </div>
          </div>
        )}

        <PlusPromptModal
          open={plusPromptOpen}
          onClose={() => setPlusPromptOpen(false)}
          deviceId={selected}
          plus={deviceData.plus}
          isOwner={currentDevice?.role === 'owner'}
        />

        {/* No reading yet */}
        {!reading && !isOffline && !isUpdating && !sharingPaused && (
          <div style={{ textAlign: 'center', padding: '40px 0' }}>
            <div className="spinner" />
            <p style={{ fontSize: 13 }}>Waiting for device data…</p>
          </div>
        )}

        {/* Free: say what they're looking at, and what Plus adds. */}
        {freeDaily && !sharingPaused && (
          <div className="free-daily-note">
            <span>
              Updates once a day.
              {currentDevice?.role === 'owner' && (
                <>
                  {' '}For live data,{' '}
                  <button type="button" className="linklike" onClick={() => setPlusPromptOpen(true)}>upgrade to SoKo Plus</button>.
                </>
              )}
            </span>
          </div>
        )}

        {sharingPaused && <SharingPaused ownerEmail={deviceData.ownerEmail} />}

        {/* One "Updated" line for the monitor (both tanks share the reading). */}
        {shown && !sharingPaused && shownMs && (
          <div className={`tank-updated ${shown.s2 ? 'tank-updated--two' : ''} ${isOffline ? 'is-offline' : ''}`}>
            {isOffline ? 'Last seen' : 'Updated'} · {formatHST(shownMs)}
          </div>
        )}

        {/* Tank cards */}
        {shown && !sharingPaused && (
          <div className={`tank-grid ${shown.s2 ? 'tank-grid--two' : ''}`}>
            <TankCard
              tankName={(deviceData.tank1?.name && deviceData.tank1.name !== 'Tank 1') ? deviceData.tank1.name : (isSingleTank ? displayName : 'Tank 1')}
              tank={deviceData.tank1}
              reading={shown}
              sensorPresent={true}
              fault={!readingFromCache && (shown.f1 ?? false)}
              pct={shown.p1 ?? 0}
              mA={shown.mA1 ?? 4}
              depthFormat={prefs.depthFormat}
              alerts={currentDevice?.alerts}
              offline={isOffline}
              leak={leakStatus[1]}
              onLeakClick={setLeakModal}
              supply={deviceData.supply?.tank1}
              supplyOn={deviceData.features?.supplyTrial === true}
              supplyLocked={!plusIsOn(deviceData.plus)}
              onSupplyLocked={() => setPlusPromptOpen(true)}
              supplyLabel={shown.s2 ? (deviceData.tank1?.name || 'Tank 1') : null}
              onSetup={() => navigate(`/setup/${currentDevice?.deviceId}/1`, { state: { returnTo: '/dashboard' } })}
            />

            {shown.s2 ? (
              <TankCard
                tankName={deviceData.tank2?.name || 'Tank 2'}
                tank={deviceData.tank2}
                reading={shown}
                sensorPresent={true}
                fault={!readingFromCache && (shown.f2 ?? false)}
                pct={shown.p2 ?? 0}
                mA={shown.mA2 ?? 4}
                  depthFormat={prefs.depthFormat}
                alerts={currentDevice?.alerts}
                offline={isOffline}
                leak={leakStatus[2]}
                onLeakClick={setLeakModal}
                supply={deviceData.supply?.tank2}
                supplyOn={deviceData.features?.supplyTrial === true}
                supplyLocked={!plusIsOn(deviceData.plus)}
                onSupplyLocked={() => setPlusPromptOpen(true)}
                supplyLabel={deviceData.tank2?.name || 'Tank 2'}
                onSetup={() => navigate(`/setup/${currentDevice?.deviceId}/2`, { state: { returnTo: '/dashboard' } })}
              />
            ) : isSingleTank ? null : (
              <div className="sensor-absent">
                <div className="sensor-absent-dot" />
                <span className="sensor-absent-label">
                  Tank 2 · no sensor detected
                </span>
              </div>
            )}
          </div>
        )}

      </div>

      <LeakModal
        open={!!leakModal}
        // Looked up live by tank, so an open modal updates as the event does
        // (running total, dropping → confirming → settled).
        leak={leakModal ? (leakStatus[leakModal.leak?.tankNum] || leakModal.leak) : null}
        tankName={leakModal?.tankName}
        pct={leakModal?.pct}
        irrigation={currentDevice?.alerts?.away?.irrigation}
        onClose={() => setLeakModal(null)}
        onDismiss={dismissLeak}
        onViewChart={viewLeakOnChart}
        singleTank={isSingleTank}
        onManageAlerts={(focus) => { setLeakModal(null); navigate(`/alerts${focus ? `?focus=${focus}` : ''}`); }}
      />

      <BottomNav />
      <InstallPrompt ready={!!reading} />
      <GuidedTour steps={TOUR_STEPS} run={tourRun} onFinish={finishTour} />
    </div>
  );
}